import React from 'react';
import { motion } from 'framer-motion';
import { Star, Check, ChevronRight, Dumbbell, Waves, Wind, Users, Calendar, Clock, Target, Heart, Sparkles } from 'lucide-react';

const membershipBenefits = [
  {
    icon: Dumbbell,
    title: 'Full Gym Access',
    description: 'Complete access to our premium gym floor with state-of-the-art equipment',
    features: ['Cardio machines', 'Free weights', 'Functional training area', 'Strength equipment']
  },
  {
    icon: Users,
    title: 'Group Fitness Classes',
    description: 'Unlimited access to all our expert-led group fitness classes',
    features: ['HYROX Training Club', 'Pilates', 'Yoga', 'Strength Club']
  },
  {
    icon: Waves,
    title: 'Recovery Zone',
    description: 'Full access to our luxury wellness and recovery facilities',
    features: ['25m swimming pool', 'Spa pool', 'Ice bath therapy', 'Traditional sauna', 'Steam room']
  },
  {
    icon: Target,
    title: 'Personal Training',
    description: 'Complimentary 60-minute session with our expert trainers',
    features: ['Fitness assessment', 'Program design', 'Technique coaching', 'Goal setting']
  }
];

const additionalPerks = [
  'Complimentary towel service',
  'Free fitness assessment',
  'Personalized program design',
  'No joining fee',
  'No key tag fee',
  'Luxury changing rooms',
  'Expert guidance from qualified trainers'
];

export default function GatewayMembership() {
  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-100 text-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
            className="inline-block mb-6"
          >
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-1 rounded-full shadow-lg shadow-blue-500/20">
              <div className="bg-white px-6 py-2 rounded-full flex items-center space-x-2">
                <Star className="w-5 h-5 text-blue-500" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500 font-bold">
                  MOST POPULAR MEMBERSHIP
                </span>
                <Star className="w-5 h-5 text-blue-500" />
              </div>
            </div>
          </motion.div>

          <h2 className="text-5xl sm:text-7xl font-bold mb-4">GATEWAY MEMBERSHIP</h2>
          <p className="text-xl text-gray-600 mb-8">Everything you need for your fitness journey</p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
            <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl p-6">
              <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500 mb-2">
                $44.50
              </div>
              <div className="text-gray-600">per week</div>
              <div className="text-sm text-gray-500 mt-1">6-month term</div>
            </div>
            <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl p-6">
              <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500 mb-2">
                $49.50
              </div>
              <div className="text-gray-600">per week</div>
              <div className="text-sm text-gray-500 mt-1">Flexible term</div>
            </div>
          </div>
        </motion.div>

        {/* Main Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {membershipBenefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.8 }}
              className="relative group"
            >
              <motion.div
                animate={{
                  opacity: [0.3, 0.6, 0.3],
                  scale: [1, 1.02, 1]
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              />
              <div className="relative bg-white rounded-2xl p-8 shadow-lg border border-gray-200 h-full transform hover:scale-105 transition-transform duration-300">
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 p-4 rounded-xl w-fit mb-6">
                  <benefit.icon className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-gray-600 mb-6">{benefit.description}</p>
                <ul className="space-y-2">
                  {benefit.features.map((feature, i) => (
                    <li key={i} className="flex items-center space-x-2">
                      <Check className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Perks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-br from-blue-500/5 to-cyan-500/5 rounded-2xl p-8 border border-blue-500/10 mb-12"
        >
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Sparkles className="w-6 h-6 text-blue-500" />
              <h3 className="text-3xl font-bold">Additional Perks Included</h3>
              <Sparkles className="w-6 h-6 text-blue-500" />
            </div>
            <p className="text-gray-600">Everything you need for a premium fitness experience</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {additionalPerks.map((perk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
                className="flex items-center space-x-3 bg-white rounded-xl p-4 shadow-sm transform hover:scale-105 transition-transform duration-300"
              >
                <Check className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <span className="text-gray-700">{perk}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl p-8 text-white relative overflow-hidden">
            <motion.div
              animate={{
                opacity: [0.3, 0.6, 0.3],
                scale: [1, 1.1, 1]
              }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 blur-3xl"
            />
            
            <div className="relative">
              <h3 className="text-3xl font-bold mb-4">Ready to Start Your Journey?</h3>
              <p className="text-xl mb-8 opacity-90">Join hundreds of members who've transformed their lives with Gateway membership</p>
              
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="https://thefitnessportal.gymmasteronline.com/portal/membership/c64f870aaf8ca324658ff95df6ca5ee8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 group"
                >
                  <span>JOIN WITH 6-MONTH TERM</span>
                  <ChevronRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                </a>
                <a
                  href="https://thefitnessportal.gymmasteronline.com/portal/membership/2035a3fbf72ef20957c9e71a2b8ebf77"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-all duration-300"
                >
                  <span>FLEXIBLE MEMBERSHIP</span>
                </a>
              </div>
              
              <div className="mt-6 text-sm opacity-75">
                <p>✓ No lock-in contracts available ✓ Cancel anytime with 30 days notice</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}